import { Component, Inject, Input, LOCALE_ID, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import { Course } from "src/app/_models/Course";
import { LearnerCourse } from "src/app/_models/LearnerCourse";
import { LearnerCourseLog } from "src/app/_models/LearnerCourseLog";
import { Userprofile } from "src/app/_models/Userprofile";
import { LearnerCourseLogService } from "src/app/_services/learner-course-log.service";
import { LearnerCourseService } from "src/app/_services/learner-course.service";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { UserSessionService } from "src/app/_services/user-session.service";
import { GetCourseById } from "src/app/_store/course/course.actions";
import { selectCourse, selectCourseListLoader } from "src/app/_store/course/course.selectors";
import { GenerateLearningPlan, ReAssignLearnerToAssessment } from "src/app/_store/learner-course/learner-course.actions";
import { selectGenerateLearningPlanLoader, selectGenerateLearningPlanResult, selectReAssignLoader, selectReAssignResult } from "src/app/_store/learner-course/learner-course.selectors";
import { selectOwnUserProfile } from "src/app/_store/user-profile/user-profile.selectors";
import { AppConstants } from "src/app/constants/constants";
import { Modes } from "src/app/constants/modes";

@Component({
  selector: "app-assign-target",
  templateUrl: "./assign-target.component.html",
  styleUrls: ["./assign-target.component.scss"],
})
export class AssignTargetComponent implements OnInit {
  destroy$: Subject<void> = new Subject<void>();
  @Input() courseId: number;
  @Input() learnerId: number;
  @Input() lcLogId: number;
  course$: Observable<Course>;
  courseLoader$: Observable<boolean>;
  reAssignLoader$: Observable<boolean>;
  generatePlanLoader$: Observable<boolean>;
  isCheckingLearningPlan: boolean = false;
  siteLanguageCode: string = this.localeId;
  userFirstName: string;
  userLastName: string;
  learnerCourse: LearnerCourse;
  courseObjectId: string;
  initialLoad: boolean = true;
  constructor(
    @Inject(LOCALE_ID) protected localeId: string,
    private store: Store,
    private publicUserSessionService: PublicUserSessionService,
    private router: Router,
    private learnerCoursesService: LearnerCourseService,
    private userSessionService: UserSessionService,
    private learnerCourseLogService: LearnerCourseLogService
  ) {}

  ngOnInit(): void {
    this.selectorCourse();
    this.selectorReAssignLoader();
    this.selectorGeneratePlanLoader();
    this.selectorCourseLoader();
    this.selectorReAssignResult();
    this.selectorGenerateLearningPlanResult();
    this.selectorOwnUserProfile();

    this.pipeScormLoader();

    this.dispatchGetCourse();
    this.getLearnerCourse();
  }

  pipeScormLoader(): void {
    this.userSessionService.onDisplayLoader$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loader: boolean) => {
        if(loader == false) {
          if(this.initialLoad) {
            this.initialLoad = false;
          } else {
            setTimeout(() => {
              this.router.navigate([AppConstants.LEARNING_PAGE], {
                queryParamsHandling: AppConstants.MERGE,
              });
            }, 6000);
          }
        }
      });
  }

  dispatchGetCourse(): void {
    this.store.dispatch(GetCourseById({courseId: this.courseId}));
  }

  selectorOwnUserProfile(): void {
    this.store.select(selectOwnUserProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: Userprofile) => {
        if(profile) {
          this.userFirstName = profile.firstName;
          this.userLastName = profile.lastName;
        }
      });
  }

  selectorCourse(): void {
    this.course$ = this.store.select(selectCourse);

    this.course$.pipe(takeUntil(this.destroy$))
      .subscribe((result: Course) => {
        if(result) {
          this.courseObjectId = result.initialAssessmentObjectId;
        }
      })
  }

  selectorReAssignLoader(): void {
    this.reAssignLoader$ = this.store.select(selectReAssignLoader);
  }

  selectorGeneratePlanLoader(): void {
    this.generatePlanLoader$ = this.store.select(selectGenerateLearningPlanLoader);
  }

  selectorCourseLoader(): void {
    this.courseLoader$ = this.store.select(selectCourseListLoader);
  }

  selectorReAssignResult(): void {
    this.store.select(selectReAssignResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: number) => {
        if(result != null) {
          this.getLearnerCourseLog();
        }
      });
  }

  selectorGenerateLearningPlanResult(): void {
    this.store.select(selectGenerateLearningPlanResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: number) => {
        if(result != null) {
          this.navigateToLearningPage();
        }
      });
  }

  onViewInitialAssessment(): void {
    this.isCheckingLearningPlan = true;
    this.learnerCourseLogService
      .isLearningPlanGenerated$(this.courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (isGenerated: boolean) => {
          this.isCheckingLearningPlan = false;
          if(isGenerated) {
            this.dispatchReAssignLearner();
          } else {
            this.dispatchGenerateLearningPlan();
          }
        },
        () => {
          this.isCheckingLearningPlan = false;
          this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        }
      );
  }

  navigateToLearningPage(): void {
    this.router.navigate([AppConstants.LEARNING_PAGE], {
      queryParamsHandling: AppConstants.MERGE,
    });
  }

  dispatchReAssignLearner(): void {
    this.store.dispatch(ReAssignLearnerToAssessment({courseId: this.courseId}));
  }

  dispatchGenerateLearningPlan(): void {
    this.store.dispatch(GenerateLearningPlan({courseId: this.courseId}));
  }

  getLearnerCourse(): void {
    this.learnerCoursesService
      .getLearnerCoursesByLanguageAlt$(this.siteLanguageCode)
      .pipe(
        catchError(() => {
          return this.router.navigate([AppConstants.DASHBOARD_PAGE]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((learnerCourse: LearnerCourse[]) => {
        this.learnerCourse = learnerCourse.find((lc) => lc.courseId == this.courseId);
      });
  }

  async redirectToContentPlayer(objectId: string): Promise<void> {
    await this.publicUserSessionService
      .setIsAllowAccessContent$(AppConstants.TRUE)
      .toPromise();

    this.router.navigate([AppConstants.CONTENT_PLAYER_PAGE + `/${objectId}`], {
      queryParams: {
        mode: Modes.Tracked,
        lcLogId: this.lcLogId,
        lcStartLevel: this.learnerCourse.startLevel,
      },
      queryParamsHandling: AppConstants.MERGE,
    });
  }

  getLearnerCourseLog(): void {
    this.learnerCourseLogService
    .getLearnerCourseLog$(this.courseId)
    .pipe(
      catchError(() => {
        return this.router.navigate([AppConstants.DASHBOARD_PAGE]);
      })
    )
    .subscribe(
      (learnerCourseLog: LearnerCourseLog) => {
        this.lcLogId = learnerCourseLog.id;
        this.redirectToContentPlayer(this.courseObjectId)
      },
      (error) => {
        console.error(error);
        this.router.navigate([AppConstants.DASHBOARD_PAGE]);
      }
    );
  }


}
